import React, { Fragment } from 'react'
import styled from 'styled-components'
import { graphql } from 'react-apollo'
import PropTypes from 'prop-types'
import DebtHeader from './DebtHeader'
import DebtRow from './DebtRow'
import DebtFooter from './DebtFooter'
import Button from '../common/Button'
import DebtScheduleQuery from './queries/DebtScheduleQuery'
import Loader from '../common/Loader'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 5px;
`
const NoDebtsContainer = styled.div`
  margin-bottom: 20px;
`
const NoDebtsMessage = styled.div`
  margin-bottom: 10px;
`
const Header = styled.h3`
  margin-top: 20px;
`
const ButtonContainer = styled.div`
  padding-bottom: 10px;
`
const AddDebt = Button.extend`
  padding: 5px 13px;
`

export class DebtSchedule extends React.Component {
  static propTypes = {
    data: PropTypes.shape({
      refetch: PropTypes.func.isRequired,
      business: PropTypes.shape({
        outstandingLoans: PropTypes.arrayOf(PropTypes.object),
      }),
    }),
    financialReviewRefetch: PropTypes.func.isRequired,
    monthsRemainingCutoff: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
  }
  constructor() {
    super()
    this.state = {
      isEditable: false,
      editableDebt: null,
    }
  }
  toggleEdit = () => {
    this.setState({ isEditable: !this.state.isEditable })
  }
  renderNewDebt = () => {
    const { financialReviewRefetch } = this.props
    const { refetch, business } = this.props.data
    const { isEditable } = this.state
    const { rid, loanTypeOptions } = business

    if (isEditable) {
      return (
        <DebtRow
          id="debt_row_new_debt"
          onCancel={this.toggleAddDebt}
          isEditable
          refetch={refetch}
          businessId={rid}
          loanTypeOptions={loanTypeOptions}
          financialReviewRefetch={financialReviewRefetch}
          newDebt
        />
      )
    }
  }
  renderRows = () => {
    const { monthsRemainingCutoff, data, financialReviewRefetch } = this.props
    const { refetch } = data
    const { outstandingLoans, rid, loanTypeOptions } = data.business
    const { editableDebt } = this.state

    return outstandingLoans.map((loan, i) => {
      return (
        <DebtRow
          id={`debt_row_${i}`}
          key={`debt-row-${i}`}
          loan={loan}
          monthsRemainingCutoff={monthsRemainingCutoff}
          isEditable={loan.rid === editableDebt}
          enableEdit={() => this.setState({ editableDebt: loan.rid })}
          refetch={refetch}
          loanTypeOptions={loanTypeOptions}
          businessId={rid}
          onCancel={() => this.setState({ editableDebt: null })}
          financialReviewRefetch={financialReviewRefetch}
        />
      )
    })
  }
  renderFooter = () => {
    const { outstandingLoans } = this.props.data.business
    const outstandingPrincipal = outstandingLoans.reduce(
      (a, b) => a + b.principalBalance,
      0,
    )
    const monthlyPayment = outstandingLoans.reduce(
      (a, b) => a + b.monthlyPayment,
      0,
    )
    const outstandingRefinance = outstandingLoans.reduce(
      (acc, loan) => (loan.refinance ? acc + loan.principalBalance : acc),
      0,
    )
    const monthlyRefinance = outstandingLoans.reduce(
      (acc, loan) => (loan.refinance ? acc + loan.monthlyPayment : acc),
      0,
    )

    return (
      <Fragment>
        <DebtFooter
          left={`$${outstandingPrincipal.toLocaleString('en')}`}
          right={`$${monthlyPayment.toLocaleString('en')}`}
          title="Total Existing Business Loans"
        />
        <DebtFooter
          left={`$${outstandingRefinance.toLocaleString('en')}`}
          right={`$${monthlyRefinance.toLocaleString('en')}`}
          title="Total Refinance Amount"
          marginTop
        />
      </Fragment>
    )
  }
  toggleAddDebt = () => {
    this.setState({ isEditable: !this.state.isEditable })
  }
  renderHeader = () => <Header>Debt Schedule for {this.props.name}</Header>
  renderDebtSchedule = () => {
    const { outstandingLoans, noExistingDebt } = this.props.data.business
    const { isEditable } = this.state

    if (outstandingLoans.length === 0 && !isEditable) {
      return (
        <NoDebtsContainer>
          <NoDebtsMessage>
            {noExistingDebt === true
              ? 'The customer stated in the application flow that they have no existing debt.'
              : 'There is no data entered in the debt schedule.'}
          </NoDebtsMessage>
          <AddDebt id="debt_schedule_add_debt" onClick={this.toggleAddDebt}>
            Add Debt
          </AddDebt>
        </NoDebtsContainer>
      )
    } else {
      return (
        <Container>
          {!isEditable && (
            <ButtonContainer>
              <AddDebt id="debt_schedule_add_debt" onClick={this.toggleAddDebt}>
                Add Debt
              </AddDebt>
            </ButtonContainer>
          )}
          <DebtHeader />
          {this.renderRows()}
          {this.renderNewDebt()}
          {this.renderFooter()}
        </Container>
      )
    }
  }
  render() {
    const { data } = this.props

    // We only show the loading spinner if the query has been run for the first time (network status 1)
    // networkStatus enum lives in networkStatus.ts in apollo-client
    if (data.networkStatus === 1) {
      return <Loader number={4} />
    }

    if (data.error) {
      return <h3>Please create a product support ticket</h3>
    }

    return (
      <section>
        {this.renderHeader()}
        {this.renderDebtSchedule()}
        <br />
      </section>
    )
  }
}

export default graphql(DebtScheduleQuery, {
  options: (OGProps) => ({
    variables: {
      rid: OGProps.rid,
    },
  }),
})(DebtSchedule)
